import PageAnimator from '../../components/ui/PageAnimator';
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  TrendingUp,
  ShieldAlert,
  Database,
  Activity,
  Copy,
  Check,
  Eye,
  PlusCircle,
  Sparkles,
  BarChart3
} from 'lucide-react';
import MiniLoader from '../../components/MiniLoader';
import Tooltip from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useNotification } from '../../context/NotificationContext';
import {
  searchQuantalysFunds,
  getFundDetails,
  importProductToCatalogue
} from '../../features/tools/api/quantalysAPI';

const SUGGESTIONS = [
  { label: "Carmignac Patrimoine", isin: "FR0010149302" },
  { label: "Comgest Monde", isin: "FR0000295230" },
  { label: "Amundi MSCI World", isin: "CW8.PA" },
  { label: "SCPI Alpha Test", isin: "FR8427368297" }
];

export default function Quantalys() {
  const { showToast } = useNotification();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [source, setSource] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'catalogue', 'external'

  // Modale détails fonds
  const [selectedFund, setSelectedFund] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // État copie ISIN
  const [copiedIsin, setCopiedIsin] = useState(null);

  // État d'importation
  const [importingId, setImportingId] = useState(null);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setHasSearched(true);
    try {
      const data = await searchQuantalysFunds(query);
      setResults(data.results || []);
      setSource(data.source || "Multi-Sources");
    } catch (error) {
      console.error("Erreur de recherche Quantalys", error);
      showToast("Erreur lors de la recherche des fonds financiers.", "error");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSuggestion = (isin) => {
    setQuery(isin);
    // Lance la recherche directement
    setLoading(true);
    setHasSearched(true);
    searchQuantalysFunds(isin)
      .then((data) => {
        setResults(data.results || []);
        setSource(data.source || "Multi-Sources");
      })
      .catch((err) => {
        console.error(err);
        showToast("Erreur lors de la recherche.", "error");
        setResults([]);
      })
      .finally(() => setLoading(false));
  };

  const handleCopyIsin = (isin, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(isin);
    setCopiedIsin(isin);
    showToast(`Code ISIN ${isin} copié dans le presse-papier !`, "success");
    setTimeout(() => setCopiedIsin(null), 2000);
  };

  const handleOpenDetail = async (fund) => {
    setSelectedFund(fund);
    setIsDetailOpen(true);
    try {
      if (fund.isin && fund.isin !== "N/C") {
        const fullDetails = await getFundDetails(fund.isin);
        setSelectedFund((prev) => ({ ...prev, ...fullDetails }));
      }
    } catch {
      // Conserve les données existantes
    }
  };

  const handleImport = async (fund, e) => {
    if (e) e.stopPropagation();
    setImportingId(fund.id);
    try {
      const res = await importProductToCatalogue({
        nom: fund.nom,
        isin: fund.isin,
        societeGestion: fund.societeGestion,
        categorie: fund.categorie,
        srri: fund.srri,
        vl: fund.vl,
        description: `Fonds importé depuis le moteur d'analyse financière Quantalys.`
      });

      showToast(res.message || "Produit importé avec succès dans votre catalogue !", "success");

      // Mise à jour locale du statut inCatalogue
      setResults((prev) =>
        prev.map((item) =>
          item.id === fund.id ? { ...item, inCatalogue: true } : item
        )
      );

      if (selectedFund && selectedFund.id === fund.id) {
        setSelectedFund((prev) => ({ ...prev, inCatalogue: true }));
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showToast("Ce produit est déjà présent dans votre catalogue Produits.", "warning");
        setResults((prev) =>
          prev.map((item) =>
            item.id === fund.id ? { ...item, inCatalogue: true } : item
          )
        );
      } else {
        showToast(error.response?.data?.error || "Erreur lors de l'importation.", "error");
      }
    } finally {
      setImportingId(null);
    }
  };

  const formatPerf = (val) => {
    if (val === null || val === undefined) return "-";
    const num = Number(val);
    const isPositive = num >= 0;
    return (
      <span className={`font-semibold ${isPositive ? 'text-[var(--hp-success-700)]' : 'text-[var(--hp-danger-700)]'}`}>
        {isPositive ? '+' : ''}{num.toFixed(1)}%
      </span>
    );
  };

  const getSrriBadge = (srri) => {
    const s = Number(srri) || 3;
    let variant = "info";
    if (s <= 2) variant = "success";
    else if (s <= 4) variant = "warning";
    else variant = "destructive";

    return (
      <Badge variant={variant} className="font-mono text-xs px-2.5 py-0.5">
        SRRI {s}/7
      </Badge>
    );
  };

  const filteredResults = results.filter((item) => {
    if (filterType === 'catalogue') return item.inCatalogue;
    if (filterType === 'external') return !item.inCatalogue;
    return true;
  });

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen pb-24">
      <PageAnimator>
        {/* Header Premium Standardisé */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-6
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <Database className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Analyse Financière & Fonds (Quantalys)
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Moteur de recherche et d'extraction de données financières, valeurs liquidatives et import direct au catalogue.
              </p>
            </div>
          </div>

          {source && (
            <div className="relative z-10 flex items-center gap-2">
              <Badge variant="secondary" className="bg-surface text-primary shadow-hp-sm px-3 py-1.5 gap-1.5">
                <Activity className="w-3.5 h-3.5 text-[var(--hp-color-accent)]" />
                Source : {source}
              </Badge>
            </div>
          )}
        </div>

        {/* Barre de Recherche Multi-Sources */}
        <div className="workspace-card p-6 mb-6 bg-surface shadow-hp-sm border-workspace rounded-[var(--hp-radius-lg)] max-w-4xl mx-auto">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle pointer-events-none" />
              <Input
                type="text"
                placeholder="Rechercher par Code ISIN (ex: FR0010149302), Ticker ou Nom du fonds..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Button
              type="submit"
              variant="accent"
              disabled={loading || !query.trim()}
              className="h-11 px-6 shadow-hp-sm whitespace-nowrap gap-2 font-semibold"
            >
              {loading ? (
                <>
                  <Activity className="w-4 h-4 animate-spin" />
                  Recherche...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Rechercher
                </>
              )}
            </Button>
          </form>

          {/* Suggestions d'ISINs rapides */}
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-workspace text-xs text-subtle">
            <span className="font-semibold text-primary flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-[var(--hp-color-accent)]" />
              Suggestions rapides :
            </span>
            {SUGGESTIONS.map((s) => (
              <button
                key={s.isin}
                type="button"
                onClick={() => handleSelectSuggestion(s.isin)}
                className="px-2.5 py-1 rounded-full border border-workspace bg-surface-muted hover:bg-surface hover:text-primary transition-colors cursor-pointer text-xs font-medium"
              >
                {s.label} ({s.isin})
              </button>
            ))}
          </div>
        </div>

        {/* Résultats */}
        {loading ? (
          <div className="py-24 flex justify-center">
            <MiniLoader />
          </div>
        ) : hasSearched ? (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="workspace-card-elevated bg-surface overflow-hidden"
          >
            {/* Barre de filtrage des résultats */}
            <div className="p-4 sm:p-5 border-b border-workspace flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-surface">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[var(--hp-color-accent)]" />
                <h2 className="text-base font-bold text-primary">
                  Résultats trouvés ({filteredResults.length})
                </h2>
              </div>

              {/* Filtres rapides */}
              <div className="flex items-center gap-1.5">
                <Button
                  variant={filterType === 'all' ? 'accent' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterType('all')}
                  className="text-xs h-8"
                >
                  Tous ({results.length})
                </Button>
                <Button
                  variant={filterType === 'catalogue' ? 'accent' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterType('catalogue')}
                  className="text-xs h-8"
                >
                  Au catalogue ({results.filter(r => r.inCatalogue).length})
                </Button>
                <Button
                  variant={filterType === 'external' ? 'accent' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterType('external')}
                  className="text-xs h-8"
                >
                  Disponibles à l'import ({results.filter(r => !r.inCatalogue).length})
                </Button>
              </div>
            </div>

            {filteredResults.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-surface-muted text-subtle font-semibold text-xs uppercase tracking-wider border-b border-workspace">
                    <tr>
                      <th className="px-6 py-3.5">Fonds & Société de Gestion</th>
                      <th className="px-6 py-3.5 text-center">Code ISIN</th>
                      <th className="px-6 py-3.5 text-center">Profil Risque</th>
                      <th className="px-6 py-3.5 text-right">Dernière VL</th>
                      <th className="px-6 py-3.5 text-right">Perf. YTD</th>
                      <th className="px-6 py-3.5 text-right">Perf. 1 An</th>
                      <th className="px-6 py-3.5 text-right">Perf. 3 Ans</th>
                      <th className="px-6 py-3.5 text-center">Statut</th>
                      <th className="px-6 py-3.5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-workspace bg-surface">
                    {filteredResults.map((fund) => (
                      <tr
                        key={fund.id}
                        onClick={() => handleOpenDetail(fund)}
                        className="hover:bg-surface-muted transition-colors cursor-pointer group"
                      >
                        <td className="px-6 py-4">
                          <p className="font-bold text-primary truncate max-w-xs">{fund.nom}</p>
                          <p className="text-xs text-subtle truncate max-w-xs mt-0.5">
                            {fund.societeGestion} • {fund.categorie}
                          </p>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-surface-muted border border-workspace rounded font-mono text-xs text-primary font-semibold">
                            <span>{fund.isin}</span>
                            <button
                              type="button"
                              onClick={(e) => handleCopyIsin(fund.isin, e)}
                              className="text-subtle hover:text-primary transition-colors p-0.5"
                              title="Copier le code ISIN"
                            >
                              {copiedIsin === fund.isin ? (
                                <Check className="w-3.5 h-3.5 text-[var(--hp-success-600)]" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </div>
                        </td>

                        <td className="px-6 py-4 text-center">
                          {getSrriBadge(fund.srri)}
                        </td>

                        <td className="px-6 py-4 text-right font-semibold text-primary">
                          {fund.vl}
                        </td>

                        <td className="px-6 py-4 text-right">
                          {formatPerf(fund.perfYTD)}
                        </td>

                        <td className="px-6 py-4 text-right">
                          {formatPerf(fund.perf1A)}
                        </td>

                        <td className="px-6 py-4 text-right">
                          {formatPerf(fund.perf3A)}
                        </td>

                        <td className="px-6 py-4 text-center">
                          {fund.inCatalogue ? (
                            <Badge variant="success" className="gap-1 text-[11px]">
                              <Check className="w-3 h-3" />
                              Au catalogue
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[11px]">
                              Nouveau
                            </Badge>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Tooltip content="Fiche technique">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDetail(fund)}
                                className="h-8 w-8 text-secondary hover:text-primary"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Tooltip>

                            {!fund.inCatalogue ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => handleImport(fund, e)}
                                disabled={importingId === fund.id}
                                className="gap-1.5 h-8 text-xs font-semibold hover:bg-[var(--hp-color-accent)] hover:text-white transition-colors"
                              >
                                {importingId === fund.id ? (
                                  <Activity className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <PlusCircle className="w-3.5 h-3.5" />
                                )}
                                Importer
                              </Button>
                            ) : (
                              <Badge variant="outline" className="text-xs text-subtle">
                                Référencé
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 flex flex-col items-center justify-center text-subtle">
                <ShieldAlert className="w-12 h-12 mb-3 opacity-30" />
                <p className="font-semibold text-primary text-base">Aucun fonds ne correspond à ce filtre.</p>
                <p className="text-xs text-subtle mt-1">Modifiez vos termes de recherche ou sélectionnez un autre filtre.</p>
              </div>
            )}
          </motion.div>
        ) : (
          /* État initial d'attente */
          <div className="py-20 flex flex-col items-center justify-center text-center opacity-70">
            <div className="h-16 w-16 rounded-full bg-surface-muted flex items-center justify-center mb-4 text-tertiary">
              <BarChart3 className="w-8 h-8" />
            </div>
            <p className="font-bold text-lg text-primary">Prêt pour la recherche de fonds</p>
            <p className="text-sm text-subtle max-w-sm mt-1">
              Saisissez un code ISIN, un nom ou cliquez sur une suggestion ci-dessus pour interroger les bases financières et votre catalogue.
            </p>
          </div>
        )}

        {/* MODALE DÉTAIL D'UN FONDS */}
        <Dialog open={isDetailOpen} onClose={() => setIsDetailOpen(false)} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-ink-500/75 backdrop-blur-sm" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <DialogPanel className="bg-surface rounded-xl shadow-xl w-full max-w-2xl border border-workspace overflow-hidden">
              {selectedFund && (
                <div>
                  <div className="p-6 border-b border-workspace bg-surface">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <DialogTitle as="h3" className="text-lg font-bold text-primary">
                          {selectedFund.nom}
                        </DialogTitle>
                        <p className="text-xs text-subtle mt-1">
                          {selectedFund.societeGestion} • {selectedFund.categorie}
                        </p>
                      </div>
                      <div className="shrink-0">
                        {getSrriBadge(selectedFund.srri)}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Grille des caractéristiques */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace">
                        <p className="text-[11px] font-semibold text-subtle uppercase">Code ISIN</p>
                        <p className="font-mono text-sm font-bold text-primary mt-1">{selectedFund.isin}</p>
                      </div>
                      <div className="p-3 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace">
                        <p className="text-[11px] font-semibold text-subtle uppercase">Dernière VL</p>
                        <p className="text-sm font-bold text-primary mt-1">{selectedFund.vl}</p>
                      </div>
                      <div className="p-3 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace">
                        <p className="text-[11px] font-semibold text-subtle uppercase">Perf. YTD</p>
                        <p className="text-sm font-bold mt-1">{formatPerf(selectedFund.perfYTD)}</p>
                      </div>
                      <div className="p-3 bg-surface-muted rounded-[var(--hp-radius-md)] border border-workspace">
                        <p className="text-[11px] font-semibold text-subtle uppercase">Perf. 3 Ans</p>
                        <p className="text-sm font-bold mt-1">{formatPerf(selectedFund.perf3A)}</p>
                      </div>
                    </div>

                    {/* Description et Stratégie */}
                    <div className="space-y-2">
                      <h4 className="text-xs font-bold text-primary uppercase">Politique d'investissement</h4>
                      <p className="text-xs text-subtle leading-relaxed bg-surface-muted p-3.5 rounded-[var(--hp-radius-md)] border border-workspace">
                        {selectedFund.description ||
                          "Ce fonds vise une valorisation du portefeuille sur l'horizon de placement recommandé à travers une gestion active et diversifiée selon les critères ESG et la réglementation financière européenne."}
                      </p>
                    </div>

                    {/* Statut Catalogue */}
                    <div className="p-3.5 rounded-[var(--hp-radius-md)] border border-workspace bg-surface flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-primary">Référencement dans le CRM</p>
                        <p className="text-xs text-subtle">
                          {selectedFund.inCatalogue
                            ? "Ce produit est déjà intégré au catalogue de votre cabinet."
                            : "Ce produit n'est pas encore enregistré dans votre catalogue d'investissement."}
                        </p>
                      </div>
                      {selectedFund.inCatalogue ? (
                        <Badge variant="success" className="gap-1">
                          <Check className="w-3.5 h-3.5" />
                          Présent
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          Disponible
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="bg-workspace px-6 py-4 flex justify-between items-center border-t border-workspace">
                    <Button
                      variant="ghost"
                      onClick={() => setIsDetailOpen(false)}
                    >
                      Fermer
                    </Button>

                    {!selectedFund.inCatalogue && (
                      <Button
                        variant="accent"
                        onClick={() => handleImport(selectedFund)}
                        disabled={importingId === selectedFund.id}
                        className="shadow-hp-sm gap-2"
                      >
                        {importingId === selectedFund.id ? (
                          <>
                            <Activity className="w-4 h-4 animate-spin" />
                            Importation en cours...
                          </>
                        ) : (
                          <>
                            <PlusCircle className="w-4 h-4" />
                            Importer dans mes Produits
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </DialogPanel>
          </div>
        </Dialog>
      </PageAnimator>
    </div>
  );
}

